import { Box, useMediaQuery, useTheme } from "@mui/material";
import type { ReactNode } from "react";

import { GenericTable } from "@/components/GenericTable/GenericTable";
import type { GenericTableProps } from "@/components/GenericTable/types";
import { usePreferMobileCards } from "../../context/EhrRecordsLayoutContext";
import { EhrSummaryMobileList } from "./EhrSummaryMobileList";

type EhrResponsiveTableProps<T extends Record<string, any>> = {
  title: string;
  columns: GenericTableProps<T>["tableColumns"];
  rows: T[];
  actions?: GenericTableProps<T>["actions"];
  characterLimit?: number;
  statusVariant?: GenericTableProps<T>["statusVariant"];
  hideSectionHeader?: boolean;
  renderCard?: (row: T, index: number) => ReactNode;
};

/** GenericTable no web; cards + preview 5 + Ver tudo no mobile do sumário. */
export function EhrResponsiveTable<T extends Record<string, any>>({
  title,
  columns,
  rows,
  actions,
  characterLimit,
  statusVariant,
  hideSectionHeader = false,
  renderCard,
}: EhrResponsiveTableProps<T>) {
  const isEhrMobile = usePreferMobileCards();

  if (isEhrMobile) {
    return (
      <EhrSummaryMobileList
        title={title}
        columns={columns}
        rows={rows ?? []}
        actions={actions}
        hideSectionHeader={hideSectionHeader}
        renderCard={renderCard}
      />
    );
  }

  return (
    <GenericTable
      tableColumns={columns}
      tableRows={rows ?? []}
      actions={actions}
      characterLimit={characterLimit}
      statusVariant={statusVariant}
    />
  );
}

interface EhrMobilePillTabsProps<T extends string> {
  tabs: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}

/** Abas pill compactas para seções tabuladas no mobile do sumário. */
export function EhrMobilePillTabs<T extends string>({
  tabs,
  value,
  onChange,
}: EhrMobilePillTabsProps<T>) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  if (!isMobileLayout) return null;

  return (
    <Box
      role="tablist"
      sx={{
        display: "flex",
        gap: 0.5,
        width: "100%",
        bgcolor: (t) => t.palette.action.hover,
        borderRadius: 2.5,
        p: 0.5,
        mb: 1.25,
        overflowX: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {tabs.map((tab) => {
        const selected = value === tab.value;
        return (
          <Box
            key={tab.value}
            role="tab"
            aria-selected={selected}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            sx={{
              flex: "0 0 auto",
              border: "none",
              cursor: "pointer",
              borderRadius: 2,
              py: 0.65,
              px: 1.1,
              fontFamily: "inherit",
              fontSize: "0.75rem",
              fontWeight: 700,
              whiteSpace: "nowrap",
              bgcolor: selected ? "background.paper" : "transparent",
              color: selected ? "primary.main" : "text.secondary",
              boxShadow: selected ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {tab.label}
          </Box>
        );
      })}
    </Box>
  );
}
